import React, { memo, useMemo, useState } from 'react';
import type { TextFieldProps } from '@mui/material';
import { createFilterOptions, TextField } from '@mui/material';
import { type FormikErrors } from 'formik';
import Autocomplete from '@mui/material/Autocomplete';
import type {
  BatchReceiptDetails,
  ReceiptBatchFormValues,
  TaxItem,
} from './ServiceProviderReceiptBatchForm';
import type { LineItemConfigurationResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useTranslation } from 'react-i18next';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

interface ServiceProviderReceiptBatchServiceSelectionProps {
  lineItem: BatchReceiptDetails['lineItems'][number];
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  setAmount: (amount: number) => void;
  setTax: (tax: TaxItem | undefined) => void;
  setName: (name: string) => void;
  index: number;
  lineItemIndex: number;
  textFieldProps?: TextFieldProps;
  setLineItemValues: (
    lineItemIndex: number,
    name: string,
    amount: number | undefined,
    tax: TaxItem | undefined
  ) => void;
}

export const ServiceProviderReceiptBatchServiceSelection: React.FC<ServiceProviderReceiptBatchServiceSelectionProps> =
  memo(
    ({
      lineItem,
      lineItemConfigurations,
      setAmount,
      setTax,
      setName,
      index,
      lineItemIndex,
      textFieldProps,
      setLineItemValues,
    }) => {
      const { t } = useTranslation();

      const [error, touched, setFieldTouched, individualDatesMode] =
        useReceiptBatchStore(
          useShallow((state) => [
            (
              (
                state?.formik?.errors?.receipts?.[index] as FormikErrors<
                  ReceiptBatchFormValues['receipts'][number]
                >
              )?.lineItems?.[lineItemIndex] as FormikErrors<
                ReceiptBatchFormValues['receipts'][number]['lineItems'][number]
              >
            )?.name,
            state.formik?.touched?.receipts?.[index]?.lineItems?.[lineItemIndex]
              ?.name,
            state.formik?.setFieldTouched,
            state.formik?.values.individualDatesMode,
          ])
        );

      const showError = useMemo(() => {
        return Boolean(error) && touched !== undefined;
      }, [error, touched]);

      const [open, setOpen] = useState(false);

      const autoCompleteOptions = useMemo(
        () =>
          lineItemConfigurations?.map((item) => ({
            value: item as LineItemConfigurationResultDto | undefined,
            label: item.name,
          })),
        [lineItemConfigurations]
      );

      const chosenAutoCompleteOption = useMemo(
        () =>
          autoCompleteOptions?.find(
            (item) => item.value?.name === lineItem.name
          ) ?? {
            label: lineItem.name ?? '',
            value: undefined,
          },
        [lineItem.name, autoCompleteOptions]
      );

      return (
        <Autocomplete
          value={chosenAutoCompleteOption}
          open={open}
          freeSolo={true}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
            setFieldTouched?.(
              `receipts.${index}.lineItems.${lineItemIndex}.name`
            );
          }}
          onChange={(_, option) => {
            setOpen(false);
            if (
              option !== undefined &&
              typeof option === 'object' &&
              option.value !== undefined
            ) {
              setLineItemValues(
                lineItemIndex,
                option.value.name,
                option.value.amount,
                option.value.taxCodes?.length
                  ? {
                      ...option.value.taxCodes[0],
                      taxAmountInPercentage:
                        option.value.taxCodes[0].taxAmount * 100,
                    }
                  : undefined
              );
            }
          }}
          filterOptions={createFilterOptions({ limit: 7 })}
          ListboxProps={{
            style: {
              maxHeight: '354px',
            },
          }}
          inputValue={lineItem.name ?? ''}
          onInputChange={(_, value) => {
            setName(value);
          }}
          isOptionEqualToValue={(option, value) =>
            option.value !== undefined &&
            value.value !== undefined &&
            option.value.name === value.value.name
          }
          disableClearable={true}
          renderInput={({ ...params }) => (
            <TextField
              {...params}
              label={t('receipts.batch.form.line-item-name')}
              name={`receipts.${index}.lineItems.${lineItemIndex}.name`}
              InputLabelProps={{
                sx: {
                  backgroundColor: 'transparent',
                  top: -3,
                },
                shrink: true,
              }}
              variant="standard"
              sx={{
                width: individualDatesMode ? 250 : 380,
              }}
              error={showError}
              helperText={showError ? error : undefined}
              {...textFieldProps}
            />
          )}
          options={autoCompleteOptions ?? []}
        />
      );
    }
  );
